<script setup lang="ts">
import { vAutoAnimate } from '@formkit/auto-animate/vue';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@pkg/ui/components/ui/form';
import type { CmsIconName } from '@pkg/cms-icons';
import { FormFieldText, FormFieldSelect } from '@pkg/ui/components/ui/form/fields';
import UiButton from '@pkg/ui/components/ui/button/ButtonUi.vue';
import { useSolutionFinder } from './useSolutionFinder';
import VendorTypePanel from './VendorTypePanel.vue';

const props = defineProps<{
  vendorTypes: Array<{
    _id: string;
    title: string;
    icon: CmsIconName;
  }>;
  stepOneTitle?: string | null;
  stepTwoTitle?: string | null;
  changeButtonText?: string | null;
  countryFieldTitle?: string | null;
  postcodeFieldTitle?: string | null;
  submitButtonText?: string | null;
}>();

const stepOneTitle = props.stepOneTitle || 'What\'s the main thing you sell?';
const stepTwoTitle = props.stepTwoTitle || 'Where are you based?';
const changeButtonText = props.changeButtonText || 'Change';
const countryFieldTitle = props.countryFieldTitle || 'Country';
const postcodeFieldTitle = props.postcodeFieldTitle || 'Add a postcode';
const submitButtonText = props.submitButtonText || 'Submit';

const { initialCountry, showCountrySelect, countries, form, onSubmit } = useSolutionFinder();
const showPostcodeSection = ref(false);
</script>

<template>
  <form @submit="onSubmit">
    <div class="wrapper">
      <FormField name="vendorType">
        <FormItem id="vendorType" v-auto-animate class="items-center">
          <div class="flex flex-col justify-start gap-[20px] items-center">
            <div class="wrapper max-w-[792px] text-center">
              <FormLabel class="c-form-label text-h3 uppercase theme-heading text-center">
                {{ stepOneTitle }}
              </FormLabel>
            </div>
            <FormControl>
              <div class="panels">
                <VendorTypePanel
                  v-for="type in props.vendorTypes"
                  :key="type.title"
                  :vendor-type="type.title"
                  :icon-name="type.icon"
                  :selected="form.values.vendorTypeId === type._id"
                  @select="form.setFieldValue('vendorTypeId', type._id)"
                />
              </div>
            </FormControl>
            <FormMessage />
          </div>
        </FormItem>
      </FormField>
    </div>

    <div class="wrapper mt-[80px]">
      <div class="flex flex-col justify-start gap-[1.25rem]">
        <div class="hide-country-form wrapper max-w-[792px] text-center">
          <label for="countryIso-form-item" class="text-h3 uppercase theme-heading">
            {{ stepTwoTitle }}
          </label>
        </div>
        <div class="wrapper max-w-[440px] text-center">
          <div class="hide-country-form flex flex-col justify-start gap-[20px]">
            <div v-if="!showCountrySelect && initialCountry">
              <p class="initial-country">
                {{ initialCountry.title }}
              </p>
              <button class="mt-[4px] underline theme-heading" type="button" @click="showCountrySelect = true">
                {{ changeButtonText }}
              </button>
            </div>
            <template v-else>
              <FormFieldSelect
                name="countryIso"
                :placeholder="countryFieldTitle"
                :options="countries"
                :default-value="initialCountry?.countryIso"
              >
                {{ countryFieldTitle }}
              </FormFieldSelect>
            </template>

            <FormFieldText v-if="showPostcodeSection" name="postcode" :placeholder="postcodeFieldTitle">
              {{ postcodeFieldTitle }}
            </FormFieldText>
          </div>

          <div class="flex flex-row justify-center">
            <UiButton type="submit">
              {{ submitButtonText }}
            </UiButton>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<style scoped>
@import '../../../../ui/assets/css/mixins/_mixins.index.pcss';

.hide-country-form {
  display: none;
}

.panels {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 12px;
  max-width: 996px;

  @screen desk {
    gap: 24px;
  }
}

.initial-country {
  @mixin h4;
  color: var(--solution-finder-country-selected-color);
  text-transform: uppercase;
}

.c-form-label {
  @mixin h3;
  color: var(--theme-heading-color);
  text-transform: uppercase;
}
</style>

<style>
@import '../../../../ui/assets/css/mixins/_mixins.index.pcss';

@mixin theme-dark {
  --solution-finder-country-selected-color: theme('colors.yellow.30');
}
@mixin theme-light {
  --solution-finder-country-selected-color: theme('colors.green.40');
}
</style>
